import { useState } from 'react';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { RequestParameters } from '~/backend/backend-client';
import { useSessionOrNull } from '~/contexts/SessionContext';
import { currentDoseState } from '~/state/atoms/dose/doseAtoms';
import { environmentState } from '~/state/atoms/engineAtoms';
import { primaryImageState } from '~/state/atoms/image/imageAtoms';
import { currentStructureSetState } from '~/state/atoms/structureSet/structureSetAtoms';
import {
    pseudonymizeState,
    workflowModalState,
    WorkflowTypes,
} from '~/state/atoms/viewerAtoms';
import { handleError } from '~/utils/errors';
import { NewJobData, useJobManagement } from '../../useJobManagement';
import useAnonymizer from '../useAnonymizer';
import { useDataBackend, useSessionID } from '../useBackend';

/**
 * A React hook for creating a data upload task.
 * @returns {Object} - An object containing the start function and submission state.
 */
export function useDataUpload() {
    const dataClient = useDataBackend();
    const { anonymizeBlobs } = useAnonymizer();
    const primaryImage = useRecoilValue(primaryImageState);
    const currentStructureSet = useRecoilValue(currentStructureSetState);
    const currentDoseMatrix = useRecoilValue(currentDoseState);
    const { sessionID, resetSessionID } = useSessionID();
    const pseudonymize = useRecoilValue(pseudonymizeState);
    const setWorkflowModalState = useSetRecoilState(workflowModalState);
    const environment = useRecoilValue(environmentState);
    const { addJob } = useJobManagement();
    const session = useSessionOrNull();
    const [isSubmitting, setIsSubmitting] = useState(false);

    const startUpload = async (action: string) => {
        if (isSubmitting) {
            return;
        }

        setIsSubmitting(true);
        resetSessionID();
        try {
            const blobs = [];
            if (primaryImage) {
                blobs.push(...Array.from(primaryImage.getSlicesAsDicomBlobs()));
            }

            if (currentStructureSet && environment) {
                environment.saveChanges();
                if (!session) {
                    throw new Error(
                        'No active patient session for data upload.'
                    );
                }
                blobs.push(
                    session.structureSetExportService.exportBlob(
                        currentStructureSet.dicomUid
                    )
                );
            }

            if (currentDoseMatrix) {
                blobs.push(currentDoseMatrix.dicomDose.getAsDicomBlob());
            }

            const blobsToUpload = pseudonymize
                ? await anonymizeBlobs('data', blobs)
                : blobs;

            const backendParameters: RequestParameters = { action };

            if (blobsToUpload && blobsToUpload.length > 0) {
                const newJobDetails: NewJobData = {
                    id: sessionID,
                    name: 'Data upload',
                    totalItems: blobsToUpload.length,
                    type: WorkflowTypes.DATA_UPLOAD,
                    client: dataClient,
                    payload: {
                        filesToUpload: blobsToUpload.map((file) => ({ file })),
                        backendParameters,
                        pseudonymized: pseudonymize,
                    },
                };

                addJob(newJobDetails);
                setWorkflowModalState((state) => ({
                    ...state,
                    open: false,
                }));
            } else {
                throw new Error('Anonymization returned empty results.');
            }
        } catch (error) {
            handleError(error, 'An error occurred during file processing:');
        } finally {
            setIsSubmitting(false);
        }
    };

    return { startUpload, isSubmitting };
}
